<template>
  <el-card>
    <el-form :inline="true" @submit.prevent>
      <el-form-item>
        <el-input v-model.trim="keyword" placeholder="会话ID/摘要/详情" @keyup.enter="onSearch" />
      </el-form-item>
      <el-form-item>
        <el-select v-model="status" clearable placeholder="状态" style="width: 140px;">
          <el-option v-for="item in statusOptions" :key="item.value" :value="item.value" :label="item.label" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-select v-model="reason" clearable placeholder="原因" style="width: 160px;">
          <el-option value="harassment" label="骚扰/不适" />
          <el-option value="unsafe" label="低俗/违规" />
          <el-option value="ooc" label="角色崩坏" />
          <el-option value="spam" label="重复打扰" />
          <el-option value="other" label="其他" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="onSearch">搜索</el-button>
        <el-button @click="onReset">重置</el-button>
        <el-button type="success" :disabled="selectedIds.length === 0" @click="onBatchUpdate('resolved')">批量设为已解决</el-button>
        <el-button type="warning" :disabled="selectedIds.length === 0" @click="onBatchUpdate('processing')">批量设为处理中</el-button>
      </el-form-item>
    </el-form>

    <el-table :data="list" stripe border v-loading="loading" @selection-change="onSelectionChange">
      <el-table-column type="selection" width="50" />
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="user_id" label="用户ID" width="90" />
      <el-table-column prop="role_id" label="角色ID" width="90" />
      <el-table-column prop="session_id" label="会话ID" min-width="180" show-overflow-tooltip />
      <el-table-column label="原因" width="120">
        <template #default="{ row }">{{ reasonLabel(row.reason) }}</template>
      </el-table-column>
      <el-table-column prop="content_excerpt" label="消息摘要" min-width="220" show-overflow-tooltip />
      <el-table-column prop="detail" label="补充说明" min-width="180" show-overflow-tooltip />
      <el-table-column prop="handle_admin_name" label="处理人" width="120" />
      <el-table-column prop="handle_note" label="处理备注" min-width="180" show-overflow-tooltip />
      <el-table-column label="状态" width="140">
        <template #default="{ row }">
          <el-tag :type="statusTagType(row.status)">{{ statusLabel(row.status) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="创建时间" min-width="170">
        <template #default="{ row }">{{ fmt(row.created_at) }}</template>
      </el-table-column>
      <el-table-column label="处理时间" min-width="170">
        <template #default="{ row }">{{ fmt(row.handled_at) }}</template>
      </el-table-column>
      <el-table-column label="处理" width="180" fixed="right">
        <template #default="{ row }">
          <el-select :model-value="row.status" size="small" @change="(v) => onChangeStatus(row, v)" style="width: 150px;">
            <el-option v-for="item in statusOptions" :key="item.value" :value="item.value" :label="item.label" />
          </el-select>
        </template>
      </el-table-column>
    </el-table>

    <div style="margin-top: 12px; display: flex; justify-content: flex-end;">
      <el-pagination
        background
        layout="total, prev, pager, next"
        :total="total"
        :current-page="query.page"
        :page-size="query.page_size"
        @current-change="onPageChange"
      />
    </div>
  </el-card>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { adminApi } from '../api/admin'

const keyword = ref('')
const status = ref('')
const reason = ref('')
const list = ref([])
const selectedIds = ref([])
const total = ref(0)
const loading = ref(false)
const query = ref({ page: 1, page_size: 20 })
const statusOptions = [
  { value: 'open', label: '待处理' },
  { value: 'processing', label: '处理中' },
  { value: 'resolved', label: '已解决' },
  { value: 'rejected', label: '驳回' }
]
const reasonMap = {
  harassment: '骚扰/不适',
  unsafe: '低俗/违规',
  ooc: '角色崩坏',
  spam: '重复打扰',
  other: '其他'
}

const fmt = (v) => (v ? new Date(v).toLocaleString() : '-')
const statusLabel = (v) => {
  const item = statusOptions.find((x) => x.value === v)
  return item ? item.label : (v || '-')
}
const reasonLabel = (v) => reasonMap[v] || (v || '-')
const statusTagType = (s) => {
  if (s === 'resolved') return 'success'
  if (s === 'rejected') return 'info'
  if (s === 'processing') return 'warning'
  return 'danger'
}

async function load() {
  loading.value = true
  try {
    const data = await adminApi.complaints({
      keyword: keyword.value,
      status: status.value || '',
      reason: reason.value || '',
      page: query.value.page,
      page_size: query.value.page_size
    })
    list.value = data.list || []
    total.value = data.total || 0
  } catch (e) {
    ElMessage.error(e.message || '加载失败')
  } finally {
    loading.value = false
  }
}

async function onChangeStatus(row, nextStatus) {
  try {
    const noteRes = await ElMessageBox.prompt('请输入处理备注（可选）', '更新投诉状态', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      inputValue: row.handle_note || '',
      inputPlaceholder: '例如：已复核，后续调整角色提示词',
      distinguishCancelAndClose: true
    }).catch((err) => {
      if (err === 'cancel' || err === 'close') return null
      throw err
    })
    if (noteRes === null) return
    const note = noteRes?.value || ''
    await adminApi.updateComplaintStatus(row.id, nextStatus, note)
    row.status = nextStatus
    row.handle_note = note
    ElMessage.success('状态已更新')
  } catch (e) {
    ElMessage.error(e.message || '更新失败')
  }
}

function onSelectionChange(rows) {
  selectedIds.value = (rows || []).map((row) => row.id)
}

async function onBatchUpdate(nextStatus) {
  if (selectedIds.value.length === 0) return
  try {
    const noteRes = await ElMessageBox.prompt('请输入批量处理备注（可选）', '批量更新投诉状态', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      inputPlaceholder: '例如：批量复核通过',
      distinguishCancelAndClose: true
    }).catch((err) => {
      if (err === 'cancel' || err === 'close') return null
      throw err
    })
    if (noteRes === null) return
    const note = noteRes?.value || ''
    await adminApi.batchUpdateComplaintStatus(selectedIds.value, nextStatus, note)
    ElMessage.success('批量更新成功')
    selectedIds.value = []
    load()
  } catch (e) {
    ElMessage.error(e.message || '批量更新失败')
  }
}

function onSearch() {
  query.value.page = 1
  load()
}

function onReset() {
  keyword.value = ''
  status.value = ''
  reason.value = ''
  query.value.page = 1
  load()
}

function onPageChange(page) {
  query.value.page = page
  load()
}

onMounted(load)
</script>
